import { createReducer, on } from '@ngrx/store';
import { StudentRegistrationActions } from './student-registration.actions';
import { initialState } from './student-registration.state';

export const studentRegistrationReducer = createReducer(
    initialState,

    // Basic Details Actions
    on(StudentRegistrationActions.updateBasicDetails, (state, { basicDetails }) => ({
        ...state,
        basicDetails: { ...state.basicDetails, ...basicDetails },
    })),

    on(StudentRegistrationActions.setBasicDetails, (state, { basicDetails }) => ({
        ...state,
        basicDetails,
    })),

    on(StudentRegistrationActions.clearBasicDetails, (state) => ({
        ...state,
        basicDetails: initialState.basicDetails,
    })),

    // Educational Details Actions
    on(StudentRegistrationActions.updateEducationalDetails, (state, { educationalDetails }) => ({
        ...state,
        educationalDetails: { ...state.educationalDetails, ...educationalDetails },
    })),

    on(StudentRegistrationActions.setEducationalDetails, (state, { educationalDetails }) => ({
        ...state,
        educationalDetails,
    })),

    on(StudentRegistrationActions.clearEducationalDetails, (state) => ({
        ...state,
        educationalDetails: initialState.educationalDetails,
    })),

    // Professional Details Actions
    on(StudentRegistrationActions.updateProfessionalDetails, (state, { professionalDetails }) => {
        return {
            ...state,
            professionalDetails: { ...state.professionalDetails, ...professionalDetails },
        };
    }),

    on(StudentRegistrationActions.setProfessionalDetails, (state, { professionalDetails }) => ({
        ...state,
        professionalDetails,
    })),

    on(StudentRegistrationActions.clearProfessionalDetails, (state) => ({
        ...state,
        professionalDetails: initialState.professionalDetails,
    })),

    // Step Navigation Actions
    on(StudentRegistrationActions.setCurrentStep, (state, { step }) => ({
        ...state,
        currentStep: step,
    })),

    on(StudentRegistrationActions.addCompletedStep, (state, { step }) => ({
        ...state,
        completedSteps: state.completedSteps.includes(step) ? state.completedSteps : [...state.completedSteps, step],
    })),

    on(StudentRegistrationActions.removeCompletedStep, (state, { step }) => ({
        ...state,
        completedSteps: state.completedSteps.filter((s) => s !== step),
    })),

    on(StudentRegistrationActions.clearCompletedSteps, (state) => ({
        ...state,
        completedSteps: [],
    })),

    // Form Submission Actions
    on(StudentRegistrationActions.setSubmitted, (state, { isSubmitted }) => ({
        ...state,
        isSubmitted,
    })),

    on(StudentRegistrationActions.submitApplication, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),

    on(StudentRegistrationActions.submitApplicationSuccess, (state, { response }) => ({
        ...state,
        isLoading: false,
        isSubmitted: true,
        error: null,
    })),

    on(StudentRegistrationActions.submitApplicationFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Loading and Error Actions
    on(StudentRegistrationActions.setLoading, (state, { isLoading }) => ({
        ...state,
        isLoading,
    })),

    on(StudentRegistrationActions.setError, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    on(StudentRegistrationActions.clearError, (state) => ({
        ...state,
        error: null,
    })),

    // Reset Actions
    on(StudentRegistrationActions.resetState, () => initialState),

    on(StudentRegistrationActions.resetFormData, (state) => ({
        ...state,
        basicDetails: initialState.basicDetails,
        educationalDetails: initialState.educationalDetails,
        professionalDetails: initialState.professionalDetails,
        currentStep: 1,
        completedSteps: [],
        isSubmitted: false,
        error: null,
    })),

    on(StudentRegistrationActions.cancelRegistration, () => initialState),

    // Persistence Actions
    on(StudentRegistrationActions.loadFromStorage, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),

    on(StudentRegistrationActions.loadFromStorageSuccess, (state, { state: loadedState }) => ({
        ...state,
        ...loadedState,
        isLoading: false,
        error: null,
    })),

    on(StudentRegistrationActions.loadFromStorageFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Initial Data Loading Actions
    on(StudentRegistrationActions.loadInitialData, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),

    on(StudentRegistrationActions.loadInitialDataSuccess, (state, { data }) => ({
        ...state,
        basicDetails: {
            courseId: data.basicDetails?.courseId || '',
            name: data.basicDetails?.name || '',
            email: data.basicDetails?.email || '',
            phone: data.basicDetails?.mobileNumber || '',
            gender: data.basicDetails?.gender || '',
            address: data.basicDetails?.mailingAddress || '',
            accommodationRequired: data.basicDetails?.accommodationRequired || false,
            feeCategory: data.basicDetails?.feeCategory || '',
        },
        educationalDetails: {
            qualifications: data.educationalDetails
                ? [
                      {
                          highestEducation: data.educationalDetails.highestEducation || '',
                          fieldOfStudy: data.educationalDetails.fieldOfStudy || '',
                          institutionName: data.educationalDetails.institutionName || '',
                          yearOfCompletion: data.educationalDetails.yearOfCompletion || '',
                          certificate: data.educationalDetails.enrollmentMarksheet || null,
                      },
                  ]
                : [],
        },
        professionalDetails: {
            currentOrganisation: data.professionalDetails?.currentOrganisation || '',
            designation: data.professionalDetails?.designation || '',
            workExperience: data.professionalDetails?.workExperience || '',
            document: data.professionalDetails?.enrollmentDocument || null,
        },
        currentStep: data.currentStep || 1,
        completedSteps: data.completedSteps || [],
        isSubmitted: data.isSubmitted || false,
        isLoading: false,
        error: null,
    })),

    on(StudentRegistrationActions.loadInitialDataFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),
);
